import React, { RefObject, useMemo } from 'react';

import { AlloySuspense } from '@adsk/alloy-react-suspense';

import { isGroupHeader } from '../../helpers';
import {
  defaultRenderFooter,
  defaultRenderMenuItem,
  defaultRenderGroupHeader,
} from '../../helpers/defaultRenderers';
import { SuspenseFallback } from '../../layout';
import { Menu, GroupFilter } from '../../layout/Menu';
import { MenuProps } from '../../layout/Menu/Menu';
import type {
  GetItemProps,
  GetMenuProps,
  GenericItem,
  RenderMenuItem,
  InputItemGroup,
  RenderGroupHeaderProps,
} from '../../types';

type DropdownMenuProps<TItem extends GenericItem> = MenuProps & {
  menuItems: TItem[];
  getMenuProps: GetMenuProps;
  getItemProps: GetItemProps<TItem>;
  highlightedIndex?: number;
  renderMenuItem?: RenderMenuItem<TItem>;
  allowCollapsibleGroups?: boolean;
  allowGroupFiltering?: boolean;
  groupedItems?: TItem[] | InputItemGroup<TItem>[];
  setExpandedGroupKey?: (key: string) => void;
  expandedGroupKey?: string;
  showGroupItemCount?: boolean;
  renderGroupHeader?: (props: RenderGroupHeaderProps) => React.ReactNode;
  setSelectedGroupKey?: (key: string) => void;
  selectedGroupKey?: string;
  showGroupHeaders?: boolean;
  setHighlightedIndex?: (index: number) => void;
  refToFocusOnClick?: RefObject<HTMLElement>;
  showEmptyGroups?: boolean;
  dropdownId?: string;
};

const DropdownMenu = <TItem extends GenericItem>({
  menuItems,
  getItemProps,
  renderHeader,
  renderMenuItem = defaultRenderMenuItem,
  renderFooter = defaultRenderFooter,
  renderGroupHeader = defaultRenderGroupHeader,
  allowCollapsibleGroups,
  allowGroupFiltering,
  setExpandedGroupKey,
  expandedGroupKey,
  showGroupItemCount,
  selectedGroupKey,
  setSelectedGroupKey,
  showGroupHeaders = true,
  setHighlightedIndex,
  refToFocusOnClick,
  showEmptyGroups = false,
  dropdownId,
  children,
  ...props
}: DropdownMenuProps<TItem>) => {
  const { isAutocomplete } = props;
  const items = useMemo(
    () =>
      menuItems.map((item, index) => {
        if (isGroupHeader?.(item)) {
          if (
            !showGroupHeaders ||
            (!showEmptyGroups && !item.filteredItemCount)
          ) {
            return null;
          }
          return renderGroupHeader?.({
            item,
            index,
            expandedGroupKey,
            allowCollapsibleGroups,
            isAutocomplete,
            setExpandedGroupKey,
            showGroupItemCount,
          });
        }

        return renderMenuItem?.({
          key: index,
          ...getItemProps({ item }),
          item,
        });
      }),
    [
      allowCollapsibleGroups,
      expandedGroupKey,
      getItemProps,
      isAutocomplete,
      menuItems,
      renderMenuItem,
      setExpandedGroupKey,
      showGroupItemCount,
      renderGroupHeader,
      showGroupHeaders,
      showEmptyGroups,
    ],
  );

  return (
    <Menu
      {...props}
      renderFooter={renderFooter}
      renderHeader={
        allowGroupFiltering
          ? () => (
              <AlloySuspense fallback={<SuspenseFallback />}>
                <GroupFilter
                  menuItems={menuItems}
                  selectedGroupKey={selectedGroupKey}
                  setSelectedGroupKey={setSelectedGroupKey}
                  showGroupItemCount={showGroupItemCount}
                  isAutocomplete={isAutocomplete}
                  setHighlightedIndex={setHighlightedIndex}
                  refToFocusOnClick={refToFocusOnClick}
                  dropdownId={dropdownId}
                />
              </AlloySuspense>
            )
          : renderHeader
      }
    >
      {items}
      {children}
    </Menu>
  );
};

DropdownMenu.render = <TItem extends GenericItem>(
  args: DropdownMenuProps<TItem>,
) => <DropdownMenu {...args} />;

export default DropdownMenu;
